/**
 * Custom hook for managing the ProjectYardSelector component in the AppBar
 * Provides consistent project selector behavior across all pages
 *
 * AST-125 PS: this hook serves every page EXCEPT a project's own page
 * (ProjectPage renders its own selector) -- Home, Project List, Manual
 * Assets, the global map. On all of those the selector reads "Projects"
 * (PS-5), regardless of the last project visited.
 *
 * It used to show `currentProject`, which projectSlice persists to
 * localStorage -- so after login Home's selector read the last project's
 * name (AC-1). Choosing "Projects" now opens Home (PS-3) instead of just
 * clearing the selection.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setCurrentProject, fetchProjectsByCustomer } from '../store/slices/projectSlice';
import ProjectYardSelector from '../components/elements/ProjectYardSelector';

/**
 * Hook that provides a pre-configured ProjectYardSelector component
 * and handles project fetching logic
 * 
 * @returns {Object} Object containing:
 *   - ProjectSelectorComponent: React component to use in AppBar's projectSelectorProps
 *   - projectList: Array of available projects
 *   - currentProject: Currently selected project
 */
const useProjectSelector = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { projectList, currentProject, loading: projectsLoading } = useAppSelector((state) => state.project);
  const { activeCustomer } = useAppSelector((state) => state.customer);

  // Load projects for selector when customer is set and projects not yet loaded or when customer changes
  useEffect(() => {
    if (activeCustomer?.id && !projectsLoading) {
      // Always fetch if projectList is empty or if we need to ensure fresh data
      // The reducer should handle deduplication
      if (projectList.length === 0) {
        dispatch(fetchProjectsByCustomer(activeCustomer.id));
      }
    }
  }, [activeCustomer?.id, projectList.length, projectsLoading, dispatch]);

  // Create project selector component
  const ProjectSelectorComponent = useMemo(() => (
    <ProjectYardSelector
      projects={projectList}
      // PS-5: never a project here -- these are all non-project pages.
      selectedProjectId={null}
      onProjectSelect={(projectId) => {
        if (projectId) {
          navigate(`/projects/${projectId}`); // PS-4
        } else {
          dispatch(setCurrentProject(null));
          navigate('/'); // PS-3: "Projects" opens Home
        }
      }}
      placeholder="Projects"
    />
  ), [projectList, navigate, dispatch]);

  return {
    ProjectSelectorComponent,
    projectList,
    currentProject,
  };
};

export default useProjectSelector;
